/**
 * Pure tick-position generator for the Dimensions overlay.
 *
 * Given a chain (per-Part track-length + module list), returns a list of
 * positions where dimension ticks should be drawn:
 *   - one tick per module boundary (kind: 'boundary')
 *   - additionally every `intermediateStepMm` along the path when > 0
 *     (kind: 'intermediate')
 *
 * Intermediate positions that coincide with a module boundary (within
 * EPSILON_MM) are dropped — the boundary tick wins. The result is sorted
 * by position ascending.
 */

import type { BuiltChain } from './ChainBuilder.js';

export type DimensionTickKind = 'boundary' | 'intermediate';

export interface DimensionTick {
  /** Cumulative position along the part (mm). */
  positionMm: number;
  kind: DimensionTickKind;
  /** Index of the containing module in chain.modules. */
  moduleIndex: number;
  /** Distance from the start of the containing module (mm). */
  positionInModuleMm: number;
}

const EPSILON_MM = 0.5;

export function buildDimensionTicks(
  chain: BuiltChain,
  options: {
    invert?: boolean;
    intermediateStepMm?: number;
  } = {},
): DimensionTick[] {
  const { invert = false, intermediateStepMm = 0 } = options;
  if (chain.modules.length === 0) return [];

  const total = chain.trackLengthMm;
  const ticks: DimensionTick[] = [];

  // 1) Module-boundary ticks. Either at the START of each module (default)
  //    or at the END (invert). Plus an extra closing tick so the FINAL
  //    boundary of the chain is always labelled: end-of-chain when
  //    counting from starts (default), start-of-chain when counting
  //    from ends (invert). Without this the consumer sees N ticks for
  //    N+1 boundaries.
  for (let i = 0; i < chain.modules.length; i++) {
    const mod = chain.modules[i]!;
    const boundary = invert
      ? mod.startPositionInPartMm + mod.trackLengthMm
      : mod.startPositionInPartMm;
    ticks.push({
      positionMm: boundary,
      kind: 'boundary',
      moduleIndex: i,
      positionInModuleMm: invert ? mod.trackLengthMm : 0,
    });
  }
  const lastIdx = chain.modules.length - 1;
  const lastMod = chain.modules[lastIdx]!;
  if (invert) {
    // First module's start is the missing closing tick.
    const first = chain.modules[0]!;
    ticks.push({
      positionMm: first.startPositionInPartMm,
      kind: 'boundary',
      moduleIndex: 0,
      positionInModuleMm: 0,
    });
  } else {
    // Last module's end is the missing closing tick.
    ticks.push({
      positionMm: lastMod.startPositionInPartMm + lastMod.trackLengthMm,
      kind: 'boundary',
      moduleIndex: lastIdx,
      positionInModuleMm: lastMod.trackLengthMm,
    });
  }

  // 2) Intermediate ticks every intermediateStepMm.
  if (intermediateStepMm > 0) {
    for (
      let p = intermediateStepMm;
      p < total - EPSILON_MM;
      p += intermediateStepMm
    ) {
      // Skip if this lands on a module boundary already in the list.
      const onBoundary = ticks.some(
        (t) => t.kind === 'boundary' && Math.abs(t.positionMm - p) < EPSILON_MM,
      );
      if (onBoundary) continue;
      const mod = findModuleAt(chain, p);
      if (!mod) continue;
      ticks.push({
        positionMm: p,
        kind: 'intermediate',
        moduleIndex: mod.idx,
        positionInModuleMm: p - mod.module.startPositionInPartMm,
      });
    }
  }

  ticks.sort((a, b) => a.positionMm - b.positionMm);
  return ticks;
}

/**
 * Format the value text shown at a tick, given a `valueMode`. Pure helper
 * so the renderer stays simple; tests cover all three modes.
 */
export function formatDimensionValue(
  tick: DimensionTick,
  mode: 'partPosition' | 'fromModule' | 'both' = 'partPosition',
): string {
  const round = (n: number) => Math.round(n);
  if (mode === 'partPosition') return `${round(tick.positionMm)} mm`;
  if (mode === 'fromModule') return `${round(tick.positionInModuleMm)} mm`;
  return `${round(tick.positionMm)} (${round(tick.positionInModuleMm)}) mm`;
}

function findModuleAt(
  chain: BuiltChain,
  positionMm: number,
): { idx: number; module: BuiltChain['modules'][number] } | null {
  for (let i = 0; i < chain.modules.length; i++) {
    const m = chain.modules[i]!;
    if (positionMm < m.startPositionInPartMm + m.trackLengthMm)
      return { idx: i, module: m };
  }
  // Past the end — clamp to last module.
  const last = chain.modules[chain.modules.length - 1]!;
  return { idx: chain.modules.length - 1, module: last };
}
